import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Trash2, Upload, Users, Edit2, ChevronDown, ChevronUp, Info, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Teams = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPlayerDialog, setShowPlayerDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [csvText, setCsvText] = useState('');
  
  const [newTeam, setNewTeam] = useState({
    name: '',
    display_name: '',
    short_name: '',
    sport: 'soccer',
    primary_color: '#234968',
    secondary_color: '#FFFFFF'
  });
  
  const [newPlayer, setNewPlayer] = useState({
    name: '',
    number: '',
    position: ''
  });
  const [showFactsDialog, setShowFactsDialog] = useState(false);
  const [factsTarget, setFactsTarget] = useState(null); // { type: 'team' | 'player', teamId, playerId?, name }
  const [editingFacts, setEditingFacts] = useState([]);
  const [newFact, setNewFact] = useState('');

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/api/teams`, {
        headers: getAuthHeaders()
      });
      setTeams(response.data);
    } catch (error) {
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  const handleCreateTeam = async () => {
    if (!newTeam.name.trim()) {
      toast.error('Team name is required');
      return;
    }
    
    try {
      await axios.post(`${API}/api/teams`, newTeam, {
        headers: getAuthHeaders()
      });
      toast.success('Team created!');
      setShowCreateDialog(false);
      setNewTeam({ name: '', display_name: '', short_name: '', sport: 'soccer', primary_color: '#234968', secondary_color: '#FFFFFF' });
      fetchTeams();
    } catch (error) {
      toast.error('Failed to create team');
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Delete this team and all its players?')) return;
    
    try {
      await axios.delete(`${API}/api/teams/${teamId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Team deleted');
      fetchTeams();
    } catch (error) {
      toast.error('Failed to delete team');
    }
  };

  const handleAddPlayer = async () => {
    if (!newPlayer.name.trim() || !newPlayer.number) {
      toast.error('Name and number are required');
      return;
    }
    
    try {
      await axios.post(`${API}/api/teams/${selectedTeam.id}/players`, {
        ...newPlayer,
        number: parseInt(newPlayer.number)
      }, {
        headers: getAuthHeaders()
      });
      toast.success('Player added!');
      setShowPlayerDialog(false);
      setNewPlayer({ name: '', number: '', position: '' });
      fetchTeams();
    } catch (error) {
      toast.error('Failed to add player');
    }
  };

  const handleDeletePlayer = async (teamId, playerId) => {
    try {
      await axios.delete(`${API}/api/teams/${teamId}/players/${playerId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Player removed');
      fetchTeams();
    } catch (error) {
      toast.error('Failed to remove player');
    }
  };

  const handleImportCSV = async () => {
    if (!csvText.trim()) {
      toast.error('Please paste CSV data');
      return;
    }
    
    try {
      const lines = csvText.trim().split('\n');
      const players = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        // Support formats: "Name,Number,Position" or "Number,Name,Position"
        const parts = line.split(',').map(p => p.trim());
        
        if (parts.length >= 2) {
          // Check if first part is a number
          const firstIsNumber = !isNaN(parseInt(parts[0]));
          
          let name, number, position;
          if (firstIsNumber) {
            number = parseInt(parts[0]);
            name = parts[1];
            position = parts[2] || '';
          } else {
            name = parts[0];
            number = parseInt(parts[1]);
            position = parts[2] || '';
          }
          
          if (name && !isNaN(number)) {
            players.push({ name, number, position });
          }
        }
      }
      
      if (players.length === 0) {
        toast.error('No valid players found in CSV');
        return;
      }
      
      await axios.post(`${API}/api/teams/${selectedTeam.id}/players/import`, players, {
        headers: getAuthHeaders()
      });
      
      toast.success(`${players.length} players imported!`);
      setShowImportDialog(false);
      setCsvText('');
      fetchTeams();
    } catch (error) {
      toast.error('Failed to import players');
    }
  };

  const openFactsDialog = (type, team, player = null) => {
    const currentFacts = type === 'team' 
      ? (team.facts || []) 
      : (player?.facts || []);
    
    setFactsTarget({
      type,
      teamId: team.id,
      playerId: player?.id,
      name: type === 'team' ? team.name : player?.name
    });
    setEditingFacts([...currentFacts]);
    setNewFact('');
    setShowFactsDialog(true);
  };

  const addFact = () => {
    if (!newFact.trim()) return;
    setEditingFacts(prev => [...prev, newFact.trim()]);
    setNewFact('');
  };

  const removeFact = (index) => {
    setEditingFacts(prev => prev.filter((_, i) => i !== index));
  };

  const saveFacts = async () => {
    if (!factsTarget) return;
    
    try {
      const endpoint = factsTarget.type === 'team'
        ? `${API}/api/teams/${factsTarget.teamId}/facts`
        : `${API}/api/teams/${factsTarget.teamId}/players/${factsTarget.playerId}/facts`;
      
      await axios.put(endpoint, { facts: editingFacts }, {
        headers: getAuthHeaders()
      });
      
      toast.success('Facts saved!');
      setShowFactsDialog(false);
      fetchTeams();
    } catch (error) {
      toast.error('Failed to save facts');
    }
  };

  const handleLogoUpload = async (teamId, file) => {
    if (!file) return;
    
    // Validate file type
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['svg', 'png'].includes(ext)) {
      toast.error('Only SVG and PNG files are allowed');
      return;
    }
    
    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/api/teams/${teamId}/logo`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Logo uploaded!');
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    }
  };

  const handleDeleteLogo = async (teamId) => {
    try {
      await axios.delete(`${API}/api/teams/${teamId}/logo`, {
        headers: getAuthHeaders()
      });
      toast.success('Logo removed');
      fetchTeams();
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  const handlePlayerPhotoUpload = async (teamId, playerId, file) => {
    if (!file) return;
    
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['svg', 'png'].includes(ext)) {
      toast.error('Only SVG and PNG files are allowed');
      return;
    }
    
    if (file.size > 1 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 1MB');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/api/teams/${teamId}/players/${playerId}/photo`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Player photo uploaded!');
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    }
  };

  const handleDeletePlayerPhoto = async (teamId, playerId) => {
    try {
      await axios.delete(`${API}/api/teams/${teamId}/players/${playerId}/photo`, {
        headers: getAuthHeaders()
      });
      toast.success('Photo removed');
      fetchTeams();
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  const handleBackgroundUpload = async (teamId, bgType, file) => {
    if (!file) return;
    
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
      toast.error('Only JPG, PNG, and WEBP files are allowed');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/api/teams/${teamId}/background/${bgType}`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`${bgType === 'lineup' ? 'Lineup' : 'Matchday'} background uploaded!`);
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload background');
    }
  };

  const handleDeleteBackground = async (teamId, bgType) => {
    try {
      await axios.delete(`${API}/api/teams/${teamId}/background/${bgType}`, {
        headers: getAuthHeaders()
      });
      toast.success('Background removed');
      fetchTeams();
    } catch (error) {
      toast.error('Failed to remove background');
    }
  };

  const sportLabels = {
    soccer: 'Soccer',
    rugby: 'Rugby',
    gaelic_football: 'Gaelic Football',
    hurling: 'Hurling/Camogie'
  };

  const positionOptions = {
    soccer: ['GK', 'DEF', 'MID', 'FWD'],
    rugby: ['PR', 'HK', 'LK', 'FL', 'N8', 'SH', 'FH', 'CE', 'WI', 'FB'],
    gaelic_football: ['GK', 'CB', 'FB', 'HB', 'MID', 'HF', 'FF'],
    hurling: ['GK', 'CB', 'FB', 'HB', 'MID', 'HF', 'FF']
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-white font-body">Loading teams...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F172A] p-4">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-heading text-white">Team Database</h1>
              <p className="text-slate-400 text-sm font-body">Manage teams and players for lineup graphics</p>
            </div>
          </div>
          
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white font-body">
                <Plus className="w-4 h-4 mr-2" />
                New Team
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1E293B] border-slate-700 text-white">
              <DialogHeader>
                <DialogTitle className="font-heading">Create New Team</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label className="text-slate-300 font-body">Team Name (Short) *</Label>
                  <Input
                    value={newTeam.name}
                    onChange={(e) => setNewTeam({...newTeam, name: e.target.value})}
                    placeholder="e.g., Hartford"
                    className="bg-[#0F172A] border-slate-600 text-white font-body mt-1"
                  />
                  <p className="text-slate-500 text-xs mt-1">Used on scoreboards</p>
                </div>
                <div>
                  <Label className="text-slate-300 font-body">Display Name (Full)</Label>
                  <Input
                    value={newTeam.display_name}
                    onChange={(e) => setNewTeam({...newTeam, display_name: e.target.value})}
                    placeholder="e.g., Hartford Athletic Football Club"
                    className="bg-[#0F172A] border-slate-600 text-white font-body mt-1"
                  />
                  <p className="text-slate-500 text-xs mt-1">Used on graphics, reports, and lineups</p>
                </div>
                <div>
                  <Label className="text-slate-300 font-body">Abbreviation</Label>
                  <Input
                    value={newTeam.short_name}
                    onChange={(e) => setNewTeam({...newTeam, short_name: e.target.value.toUpperCase()})}
                    placeholder="e.g., HAR"
                    maxLength={4}
                    className="bg-[#0F172A] border-slate-600 text-white font-body mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 font-body">Sport</Label>
                  <Select value={newTeam.sport} onValueChange={(v) => setNewTeam({...newTeam, sport: v})}>
                    <SelectTrigger className="bg-[#0F172A] border-slate-600 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-slate-600">
                      <SelectItem value="soccer">Soccer</SelectItem>
                      <SelectItem value="rugby">Rugby</SelectItem>
                      <SelectItem value="gaelic_football">Gaelic Football</SelectItem>
                      <SelectItem value="hurling">Hurling/Camogie</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 font-body">Primary Color</Label>
                    <div className="flex gap-2 mt-1">
                      <input
                        type="color"
                        value={newTeam.primary_color}
                        onChange={(e) => setNewTeam({...newTeam, primary_color: e.target.value})}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={newTeam.primary_color}
                        onChange={(e) => setNewTeam({...newTeam, primary_color: e.target.value})}
                        className="bg-[#0F172A] border-slate-600 text-white font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300 font-body">Secondary Color</Label>
                    <div className="flex gap-2 mt-1">
                      <input
                        type="color"
                        value={newTeam.secondary_color}
                        onChange={(e) => setNewTeam({...newTeam, secondary_color: e.target.value})}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={newTeam.secondary_color}
                        onChange={(e) => setNewTeam({...newTeam, secondary_color: e.target.value})}
                        className="bg-[#0F172A] border-slate-600 text-white font-mono"
                      />
                    </div>
                  </div>
                </div>
                <Button onClick={handleCreateTeam} className="w-full bg-[#22C55E] hover:bg-[#16A34A] font-body">
                  Create Team
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Teams List */}
      <div className="max-w-4xl mx-auto space-y-4">
        {teams.length === 0 ? (
          <Card className="bg-[#1E293B] border-slate-700">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-400 font-body">No teams yet. Create your first team to get started!</p>
            </CardContent>
          </Card>
        ) : (
          teams.map((team) => (
            <Card key={team.id} className="bg-[#1E293B] border-slate-700">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Team Logo or Initials */}
                    <div className="relative group">
                      {team.logo_url ? (
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                          <img 
                            src={`${API}${team.logo_url}`} 
                            alt={team.name}
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                      ) : (
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center font-bold text-sm"
                          style={{ backgroundColor: team.primary_color, color: team.secondary_color }}
                        >
                          {team.short_name || team.name.substring(0, 3).toUpperCase()}
                        </div>
                      )}
                      {/* Logo upload overlay on hover */}
                      <label className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                        <Upload className="w-5 h-5 text-white" />
                        <input
                          type="file"
                          accept=".svg,.png"
                          className="hidden"
                          onChange={(e) => handleLogoUpload(team.id, e.target.files?.[0])}
                        />
                      </label>
                    </div>
                    <div>
                      <CardTitle className="text-white font-heading text-lg">{team.name}</CardTitle>
                      <p className="text-slate-400 text-sm font-body">
                        {sportLabels[team.sport]} • {team.players?.length || 0} players
                        {team.facts?.length > 0 && (
                          <span className="text-blue-400 ml-2">• {team.facts.length} facts</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {team.logo_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteLogo(team.id)}
                        className="text-slate-400 hover:text-red-400"
                        title="Remove logo"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openFactsDialog('team', team)}
                      className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                      title="Edit team facts"
                    >
                      <Info className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedTeam(expandedTeam === team.id ? null : team.id)}
                      className="text-slate-400 hover:text-white"
                    >
                      {expandedTeam === team.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteTeam(team.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              {expandedTeam === team.id && (
                <CardContent className="pt-0">
                  <div className="border-t border-slate-700 pt-4 mt-2">
                    {/* Player Actions */}
                    <div className="flex gap-2 mb-4">
                      <Dialog open={showPlayerDialog && selectedTeam?.id === team.id} onOpenChange={(open) => {
                        setShowPlayerDialog(open);
                        if (open) setSelectedTeam(team);
                      }}>
                        <DialogTrigger asChild>
                          <Button size="sm" className="bg-[#3B82F6] hover:bg-[#2563EB] font-body">
                            <Plus className="w-4 h-4 mr-1" />
                            Add Player
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#1E293B] border-slate-700 text-white">
                          <DialogHeader>
                            <DialogTitle className="font-heading">Add Player to {team.name}</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 pt-4">
                            <div>
                              <Label className="text-slate-300 font-body">Player Name *</Label>
                              <Input
                                value={newPlayer.name}
                                onChange={(e) => setNewPlayer({...newPlayer, name: e.target.value})}
                                placeholder="e.g., John Smith"
                                className="bg-[#0F172A] border-slate-600 text-white font-body mt-1"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-slate-300 font-body">Number *</Label>
                                <Input
                                  type="number"
                                  value={newPlayer.number}
                                  onChange={(e) => setNewPlayer({...newPlayer, number: e.target.value})}
                                  placeholder="10"
                                  className="bg-[#0F172A] border-slate-600 text-white font-body mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-slate-300 font-body">Position</Label>
                                <Select value={newPlayer.position} onValueChange={(v) => setNewPlayer({...newPlayer, position: v})}>
                                  <SelectTrigger className="bg-[#0F172A] border-slate-600 text-white mt-1">
                                    <SelectValue placeholder="Select..." />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#1E293B] border-slate-600">
                                    {positionOptions[team.sport]?.map(pos => (
                                      <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <Button onClick={handleAddPlayer} className="w-full bg-[#22C55E] hover:bg-[#16A34A] font-body">
                              Add Player
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                      
                      <Dialog open={showImportDialog && selectedTeam?.id === team.id} onOpenChange={(open) => {
                        setShowImportDialog(open);
                        if (open) setSelectedTeam(team);
                      }}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 font-body">
                            <Upload className="w-4 h-4 mr-1" />
                            Import CSV
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#1E293B] border-slate-700 text-white">
                          <DialogHeader>
                            <DialogTitle className="font-heading">Import Players from CSV</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 pt-4">
                            <div>
                              <Label className="text-slate-300 font-body">Paste CSV Data</Label>
                              <p className="text-slate-500 text-xs mb-2">Format: Name,Number,Position (one per line)</p>
                              <textarea
                                value={csvText}
                                onChange={(e) => setCsvText(e.target.value)}
                                placeholder="John Smith,10,MID&#10;Jane Doe,7,FWD&#10;Bob Wilson,1,GK"
                                rows={8}
                                className="w-full bg-[#0F172A] border border-slate-600 rounded-md p-3 text-white font-mono text-sm"
                              />
                            </div>
                            <Button onClick={handleImportCSV} className="w-full bg-[#22C55E] hover:bg-[#16A34A] font-body">
                              Import Players
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    
                    {/* Players List */}
                    {team.players?.length > 0 ? (
                      <div className="space-y-2">
                        {team.players.sort((a, b) => a.number - b.number).map((player) => (
                          <div key={player.id} className="flex items-center justify-between bg-[#0F172A] rounded-lg p-3">
                            <div className="flex items-center gap-3">
                              {/* Player Photo */}
                              <div className="relative group">
                                {player.photo_url ? (
                                  <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-slate-600">
                                    <img 
                                      src={`${API}${player.photo_url}`} 
                                      alt={player.name}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold text-sm border-2 border-slate-600">
                                    {player.number}
                                  </div>
                                )}
                                {/* Photo upload overlay */}
                                <label className="absolute inset-0 bg-black/60 rounded-full flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Upload className="w-4 h-4 text-white" />
                                  <input
                                    type="file"
                                    accept=".svg,.png"
                                    className="hidden"
                                    onChange={(e) => handlePlayerPhotoUpload(team.id, player.id, e.target.files?.[0])}
                                  />
                                </label>
                              </div>
                              <div>
                                <p className="text-white font-body">{player.name}</p>
                                <p className="text-slate-400 text-xs font-body">
                                  #{player.number}
                                  {player.position && <span className="ml-1">• {player.position}</span>}
                                  {player.facts?.length > 0 && (
                                    <span className="text-blue-400 ml-2">{player.facts.length} facts</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {player.photo_url && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeletePlayerPhoto(team.id, player.id)}
                                  className="text-slate-400 hover:text-red-400"
                                  title="Remove photo"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openFactsDialog('player', team, player)}
                                className="text-blue-400 hover:text-blue-300"
                                title="Edit player facts"
                              >
                                <Info className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeletePlayer(team.id, player.id)}
                                className="text-slate-400 hover:text-red-400"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm font-body text-center py-4">
                        No players yet. Add players individually or import from CSV.
                      </p>
                    )}

                    {/* Social Media Background Images */}
                    <div className="border-t border-slate-700 pt-4 mt-4">
                      <h4 className="text-white font-heading text-sm mb-3 flex items-center gap-2">
                        <Upload className="w-4 h-4 text-slate-400" />
                        Social Media Backgrounds
                      </h4>
                      <p className="text-slate-500 text-xs mb-3">
                        Upload background images for social media graphics (JPG, PNG, WEBP - max 5MB)
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        {/* Lineup Background */}
                        <div className="bg-[#0F172A] rounded-lg p-3">
                          <p className="text-slate-400 text-xs mb-2">Lineup Graphic</p>
                          {team.lineup_background_url ? (
                            <div className="relative group">
                              <img 
                                src={`${API}${team.lineup_background_url}`}
                                alt="Lineup background"
                                className="w-full h-20 object-cover rounded-md"
                              />
                              <div className="absolute inset-0 bg-black/60 rounded-md flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <label className="cursor-pointer bg-blue-600 px-2 py-1 rounded text-xs text-white">
                                  Change
                                  <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.webp"
                                    className="hidden"
                                    onChange={(e) => handleBackgroundUpload(team.id, 'lineup', e.target.files?.[0])}
                                  />
                                </label>
                                <button 
                                  onClick={() => handleDeleteBackground(team.id, 'lineup')}
                                  className="bg-red-600 px-2 py-1 rounded text-xs text-white"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ) : (
                            <label className="w-full h-20 border-2 border-dashed border-slate-600 rounded-md flex items-center justify-center cursor-pointer hover:border-blue-500 transition-colors">
                              <span className="text-slate-400 text-xs">Click to upload</span>
                              <input
                                type="file"
                                accept=".jpg,.jpeg,.png,.webp"
                                className="hidden"
                                onChange={(e) => handleBackgroundUpload(team.id, 'lineup', e.target.files?.[0])}
                              />
                            </label>
                          )}
                        </div>

                        {/* Matchday Background */}
                        <div className="bg-[#0F172A] rounded-lg p-3">
                          <p className="text-slate-400 text-xs mb-2">Matchday Graphic</p>
                          {team.matchday_background_url ? (
                            <div className="relative group">
                              <img 
                                src={`${API}${team.matchday_background_url}`}
                                alt="Matchday background"
                                className="w-full h-20 object-cover rounded-md"
                              />
                              <div className="absolute inset-0 bg-black/60 rounded-md flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <label className="cursor-pointer bg-blue-600 px-2 py-1 rounded text-xs text-white">
                                  Change
                                  <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.webp"
                                    className="hidden"
                                    onChange={(e) => handleBackgroundUpload(team.id, 'matchday', e.target.files?.[0])}
                                  />
                                </label>
                                <button 
                                  onClick={() => handleDeleteBackground(team.id, 'matchday')}
                                  className="bg-red-600 px-2 py-1 rounded text-xs text-white"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ) : (
                            <label className="w-full h-20 border-2 border-dashed border-slate-600 rounded-md flex items-center justify-center cursor-pointer hover:border-blue-500 transition-colors">
                              <span className="text-slate-400 text-xs">Click to upload</span>
                              <input
                                type="file"
                                accept=".jpg,.jpeg,.png,.webp"
                                className="hidden"
                                onChange={(e) => handleBackgroundUpload(team.id, 'matchday', e.target.files?.[0])}
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Facts Dialog */}
      <Dialog open={showFactsDialog} onOpenChange={setShowFactsDialog}>
        <DialogContent className="bg-[#1E293B] border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {factsTarget?.type === 'team' ? 'Team' : 'Player'} Facts: {factsTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-slate-400 text-sm font-body">
              Add interesting facts for commentators to reference during the broadcast.
            </p>
            
            {/* Existing Facts */}
            {editingFacts.length > 0 && (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {editingFacts.map((fact, index) => (
                  <div key={`fact-${index}-${fact.slice(0, 20)}`} className="flex items-start gap-2 bg-[#0F172A] p-2 rounded-lg">
                    <span className="text-slate-300 text-sm flex-1">{fact}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFact(index)}
                      className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add New Fact */}
            <div className="flex gap-2">
              <Input
                value={newFact}
                onChange={(e) => setNewFact(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addFact()}
                placeholder="Enter a fact..."
                className="bg-[#0F172A] border-slate-600 text-white font-body flex-1"
              />
              <Button
                onClick={addFact}
                disabled={!newFact.trim()}
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Save Button */}
            <Button
              onClick={saveFacts}
              className="w-full bg-[#22C55E] hover:bg-[#16A34A] font-body"
            >
              Save Facts
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Teams;
